import { useCallback, useMemo } from "react";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import type { InformationForHealthcareFormProps } from "../../forms/InformationForHealthcare/InformationForHealthcareForm.js";
import {
  InformationForHealthcareForm,
  InformationForHealthcareFormSkeleton,
} from "../../forms/InformationForHealthcare/InformationForHealthcareForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:InformationForHealthcarePage");

export type InformationForHealthcarePageProps = Record<string, unknown>;

// This component is responsible for managing the API calls and passing
// the data to the inner component.
export function InformationForHealthcarePage(
  _props: InformationForHealthcarePageProps,
) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    { data: incidentWorkItem, error: incidentWorkItemError },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  const { user } = useUser();

  if (incidentWorkItemError || user === undefined) {
    return <ErrorComponent />;
  }

  return (
    <InformationForHealthcarePageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

export type InformationForHealthcarePageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
};

export function InformationForHealthcarePageInner({
  incidentWorkItem,
  refetchWorkItem,
}: InformationForHealthcarePageInnerProps) {
  if (incidentWorkItem === undefined) {
    return <InformationForHealthcarePageInnerLoading />;
  }

  const hasSection136Form = incidentWorkItem.forms.some(
    (form) => form.formType === IncidentFormTypeSchema.enum.section136,
  );
  if (!hasSection136Form) {
    return (
      <Navigate
        replace
        to={createRoute.incidentProgressPage(
          incidentWorkItem.incidentWorkItem.id,
        )}
      />
    );
  }

  return (
    <InformationForHealthcarePageInnerLoaded
      incidentWorkItem={incidentWorkItem}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

function InformationForHealthcarePageInnerLoading() {
  return <InformationForHealthcareFormSkeleton />;
}

function InformationForHealthcarePageInnerLoaded({
  incidentWorkItem,
  refetchWorkItem,
}: Required<InformationForHealthcarePageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  // Get incident information
  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;

  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  // Get 136 information
  const section136Form = useMemo(
    () =>
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.section136,
      ),
    [incidentWorkItem.forms],
  );

  if (!section136Form) {
    throw new Error("Section 136 form not found");
  }

  const section136FormId = section136Form.id;

  // Get the form specification for the info for healthcare form (including pre-computed defaultValues)
  const formSpecification = useMemo(() => {
    const existingData =
      section136Form.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.informationForHealthcare,
      )?.data ?? {};
    return workflows[workflowVersion]?.[136].formDefinition.sections
      .find(
        (section) =>
          section.id === FormSectionSchema.enum.informationForHealthcare,
      )
      ?.buildFormSpecification({
        incidentWorkItem,
        existingData,
      });
  }, [workflowVersion, incidentWorkItem, section136Form.sections]);

  if (!formSpecification) {
    throw new Error("Form specification not found");
  }

  const { formSchema: schema, defaultValues } = formSpecification;

  // Calculate whether the original existing values were valid (before fallbacks)
  const existingValuesWereValid = useMemo(() => {
    const existingValues =
      section136Form.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.informationForHealthcare,
      )?.data ?? {};
    return schema.safeParse(existingValues).success;
  }, [schema, section136Form.sections]);

  // Should we enable auto-save?
  const enableAutoSave = useMemo(
    () =>
      workflows[workflowVersion]?.[136].formDefinition.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.informationForHealthcare,
      )?.autoSave ?? false,
    [workflowVersion],
  );

  const onAutoSave: Exclude<
    InformationForHealthcareFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (existingValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }

      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          section136FormId,
          FormSectionSchema.enum.informationForHealthcare,
          values,
          existingValuesWereValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true);
    },
    [incidentWorkItemId, existingValuesWereValid, schema, section136FormId],
  );

  const onSubmit: InformationForHealthcareFormProps<
    typeof workflowVersion
  >["onSubmit"] = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        const response = await api.incidentWorkItem.updateData(
          incidentWorkItemId,
          section136FormId,
          FormSectionSchema.enum.informationForHealthcare,
          values,
          true,
        );
        if (response.status === 204) {
          await refetchWorkItem();
          navigate(`/incident/${incidentWorkItemId}/section-complete`, {
            state: {
              type: FormSectionSchema.enum.informationForHealthcare,
            },
          });
          return;
        } else {
          throw new Error(JSON.stringify(response.data.message));
        }
      } catch (error) {
        debug("Failed to update Information For Healthcare section", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      incidentWorkItemId,
      navigate,
      refetchWorkItem,
      section136FormId,
      t,
      toastNotifications,
    ],
  );

  const onCancel: InformationForHealthcareFormProps<
    typeof workflowVersion
  >["onCancel"] = useCallback(() => {
    navigate(createRoute.incidentProgressPage(incidentWorkItemId));
  }, [incidentWorkItemId, navigate]);

  return (
    <InformationForHealthcareForm
      onAutoSave={enableAutoSave ? onAutoSave : undefined}
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={defaultValues}
      incidentWorkItem={incidentWorkItem}
      workflowVersion={workflowVersion}
    />
  );
}
